import React from "react";
import {
  Box,
  Button,
  Container,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { Link as RouterLink } from "react-router-dom";
import colors from "../assets/theme/colors";
import { landingPageStyles } from "../assets/theme/landingTheme";
import Footer from "../components/Landing/Footer";
import BrandWordmark from "../components/UI/BrandWordmark";
import brandConfig from "../shared/brandConfig.json";

const UPDATED_DATE = "June 6, 2026";

const KEY_POINTS = [
  "Cookies and similar tools help Lifelog work, stay secure, remember preferences, and understand reliability.",
  "Essential cookies may be required for sign-in, security, and core site functions.",
  "You can control cookies through browser settings and any preference tools we provide.",
  "We do not use cookies to sell personal information or run cross-context behavioral advertising.",
];

const BROWSER_LINKS = [
  {
    label: "Chrome",
    href: "https://support.google.com/chrome/answer/95647#zippy=%2Callow-or-block-cookies",
  },
  {
    label: "Microsoft cookie controls",
    href: "https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d",
  },
  {
    label: "Firefox",
    href: "https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences&redirectlocale=en-US",
  },
  {
    label: "Safari",
    href: "https://support.apple.com/en-ie/guide/safari/sfri11471/mac",
  },
  {
    label: "Edge",
    href: "https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd",
  },
  {
    label: "Opera",
    href: "https://help.opera.com/en/latest/web-preferences/",
  },
];

const AD_CHOICE_LINKS = [
  { label: "Digital Advertising Alliance", href: "http://www.aboutads.info/choices/" },
  { label: "Digital Advertising Alliance of Canada", href: "https://youradchoices.ca/" },
  { label: "European Interactive Digital Advertising Alliance", href: "http://www.youronlinechoices.com/" },
];

const POLICY_SECTIONS = [
  {
    title: "1. What this policy covers",
    body: [
      "This Cookie Policy explains how Lifelog uses cookies and similar technologies when you visit lifelog.care or use our related services.",
      "It explains what these technologies are, why we use them, and how you can control them.",
      "Some cookies may collect personal information, or information that becomes personal information when combined with other details.",
    ],
  },
  {
    title: "2. What cookies are",
    body: [
      "Cookies are small data files placed on your computer or mobile device when you visit a website.",
      "First-party cookies are set by the website owner. Third-party cookies are set by another provider that supports features, infrastructure, analytics, or other services.",
      "Similar technologies may include local storage, pixels, tags, beacons, SDKs, or other tools that remember settings or help us understand service activity.",
    ],
  },
  {
    title: "3. Why we use cookies",
    body: [
      "We use cookies and similar technologies to operate Lifelog, keep accounts secure, support sign-in, prevent misuse, remember preferences, and maintain reliable service performance.",
      "We may also use these tools to understand crashes, errors, browser compatibility, feature usage, and general service reliability.",
      "We try to keep cookie use tied to practical service needs for a calm, trustworthy care-logging experience.",
    ],
  },
  {
    title: "4. Essential cookies",
    body: [
      "Some cookies are strictly necessary for the website or service to work.",
      "Essential cookies may support authentication, session management, fraud prevention, security, load balancing, and basic site functionality.",
      "Because these cookies are needed to provide the service, they may not be available to reject through a preference control.",
    ],
  },
  {
    title: "5. Analytics, performance, and diagnostics",
    body: [
      "We may use analytics, performance, crash reporting, and diagnostic tools to understand whether Lifelog is working correctly and where users run into issues.",
      "These tools help us improve reliability, fix bugs, monitor service health, and understand usage trends at a product level.",
      "Where law requires consent or a specific opt-out for optional analytics, we will provide the required choice.",
    ],
  },
  {
    title: "6. Third-party cookies",
    body: [
      "Third-party providers may set cookies or similar technologies when they help us provide hosting, authentication, security, analytics, crash reporting, or other infrastructure.",
      "These providers may recognize your browser or device when their technology is used on our website or service.",
      "We use service providers to support Lifelog, and their processing is also described in our Privacy Policy.",
    ],
  },
  {
    title: "7. Advertising cookies",
    body: [
      "We do not currently use Lifelog cookies to sell personal information or run cross-context behavioral advertising.",
      "If we introduce advertising cookies or targeted advertising technologies later, we will update this policy and provide any choices required by applicable law.",
      "You can also review industry opt-out resources below for general information about interest-based advertising choices.",
    ],
    links: AD_CHOICE_LINKS,
  },
  {
    title: "8. How you can control cookies",
    body: [
      "Where we provide a cookie banner or preference center, you can use it to choose whether to accept or reject optional cookies.",
      "You can also set your browser to remove, block, or reject cookies.",
      "If you block or delete cookies, some parts of Lifelog may not work properly, especially sign-in, preferences, and security-related features.",
    ],
  },
  {
    title: "9. Browser controls",
    body: [
      "Browser controls vary by provider. The links below explain how to manage cookies in common browsers.",
    ],
    links: BROWSER_LINKS,
  },
  {
    title: "10. Other tracking technologies",
    body: [
      "Cookies are not the only technology that can recognize or track activity. We may use similar tools such as pixels, tags, or web beacons for performance, security, email, analytics, and service reliability.",
      "For example, these tools may help us understand traffic patterns, whether an email was opened, whether a link worked, or whether a page is loading correctly.",
      "Declining cookies may limit some of these technologies when they rely on cookies to function.",
    ],
  },
  {
    title: "11. Local storage and similar tools",
    body: [
      "Lifelog may use browser storage or similar local tools to remember settings, support app functionality, or maintain a better user experience.",
      "We do not intentionally use Flash cookies or Local Shared Objects.",
      "Browser and device settings may let you clear or limit local storage and similar technologies.",
    ],
  },
  {
    title: "12. Updates to this policy",
    body: [
      "We may update this Cookie Policy from time to time to reflect changes to the cookies we use or for operational, legal, or regulatory reasons.",
      "The date at the top of this page shows when this Cookie Policy was last updated.",
      "We encourage you to review this page periodically so you understand how Lifelog uses cookies and similar technologies.",
    ],
  },
  {
    title: "13. Contact",
    body: [
      "Questions about cookies or similar technologies can be sent to the contact email below.",
      "We may need to verify your identity or account access before helping with account-specific requests.",
    ],
  },
];

const CookiePolicyPage = () => (
  <Box sx={landingPageStyles} data-cy="cookie-policy-page">
    <Box
      sx={{
        borderBottom: `1px solid ${colors.landing.borderSoft}`,
        bgcolor: alpha(colors.semantic.surface, 0.78),
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.25, md: 4 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ minHeight: { xs: 72, md: 82 } }}
        >
          <Button
            component={RouterLink}
            to="/"
            aria-label="Lifelog home"
            sx={{
              p: 0,
              minWidth: "auto",
              textTransform: "none",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <BrandWordmark variant="navbar" showDot={false} />
          </Button>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ChevronLeftRoundedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              color: colors.semantic.ink,
              borderRadius: "999px",
              px: 1.6,
            }}
          >
            Back to home
          </Button>
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ px: { xs: 2.25, md: 4 }, py: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          p: { xs: 2.4, md: 4 },
          borderRadius: "28px",
          bgcolor: colors.semantic.surface,
          border: `1px solid ${colors.landing.borderLight}`,
          boxShadow: `0 20px 54px ${alpha(colors.semantic.ink, 0.06)}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 850,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: colors.semantic.sageDeep,
          }}
        >
          Cookie Policy
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1,
            fontFamily: "'Outfit', sans-serif",
            fontSize: { xs: "2.35rem", md: "3.1rem" },
            lineHeight: 1.05,
            fontWeight: 850,
            color: colors.landing.heroText,
            letterSpacing: 0,
          }}
        >
          How Lifelog uses cookies.
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 780,
            color: colors.landing.textMuted,
            fontSize: { xs: "1rem", md: "1.08rem" },
            lineHeight: 1.65,
          }}
        >
          This policy explains the cookies and similar technologies that help Lifelog run securely and reliably.
        </Typography>
        <Typography sx={{ mt: 1.5, color: colors.landing.textSoft, fontWeight: 750 }}>
          Last updated: {UPDATED_DATE}
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: { xs: 1.8, md: 2.2 },
            borderRadius: "22px",
            bgcolor: colors.brand.tint,
            border: `1px solid ${colors.landing.borderLight}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, fontSize: "1.2rem" }}>
            At a glance
          </Typography>
          <Stack component="ul" spacing={0.8} sx={{ mt: 1.1, pl: 2.5, mb: 0 }}>
            {KEY_POINTS.map((item) => (
              <Typography component="li" key={item} sx={{ color: colors.landing.textMuted, lineHeight: 1.62, pl: 0.4 }}>
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Stack spacing={2.2} sx={{ mt: { xs: 3, md: 4 } }}>
          {POLICY_SECTIONS.map((section) => (
            <Box
              key={section.title}
              sx={{
                p: { xs: 1.8, md: 2.2 },
                borderRadius: "20px",
                bgcolor: alpha(colors.semantic.sageMist, 0.5),
                border: `1px solid ${colors.landing.borderLight}`,
              }}
            >
              <Typography component="h2" sx={{ fontSize: { xs: "1.25rem", md: "1.42rem" }, fontWeight: 900, color: colors.semantic.ink }}>
                {section.title}
              </Typography>
              <Stack component="ul" spacing={0.8} sx={{ mt: 1.1, pl: 2.5, mb: 0 }}>
                {section.body.map((item) => (
                  <Typography component="li" key={item} sx={{ color: colors.landing.textMuted, lineHeight: 1.62, pl: 0.4 }}>
                    {item}
                  </Typography>
                ))}
                {section.links?.map((item) => (
                  <Typography component="li" key={item.href} sx={{ color: colors.landing.textMuted, lineHeight: 1.62, pl: 0.4 }}>
                    <Link href={item.href} target="_blank" rel="noopener noreferrer" sx={{ color: colors.brand.deep, fontWeight: 850 }}>
                      {item.label}
                    </Link>
                  </Typography>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 3,
            p: { xs: 2, md: 2.4 },
            borderRadius: "22px",
            bgcolor: colors.brand.tint,
            border: `1px solid ${colors.landing.borderLight}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, fontSize: "1.2rem" }}>
            Questions
          </Typography>
          <Typography sx={{ mt: 0.7, color: colors.landing.textMuted, lineHeight: 1.62 }}>
            Contact us at{" "}
            <Link href={`mailto:${brandConfig.supportEmail}`} sx={{ color: colors.brand.deep, fontWeight: 850 }}>
              {brandConfig.supportEmail}
            </Link>
            .
          </Typography>
        </Box>
      </Box>
    </Container>

    <Footer variant="desktop" />
  </Box>
);

export default CookiePolicyPage;
